'use client'

import * as ToastPrimitives from '@radix-ui/react-toast'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { LuX } from 'react-icons/lu'

import { cn } from '@/utils/common'

const ToastProvider = ToastPrimitives.Provider

export const toastViewportVariants = cva(
  `fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:flex-col `,
  {
    variants: {
      position: {
        topRight: 'sm:top-0 sm:right-0 sm:bottom-auto',
        topLeft: 'sm:top-0 sm:left-0 sm:bottom-auto',
        topCenter:
          'sm:top-0 sm:left-[50%] sm:translate-x-[-50%] sm:bottom-auto',
        bottomRight: 'sm:bottom-0 sm:right-0 sm:top-auto',
        bottomLeft: 'sm:bottom-0 sm:left-0 sm:top-auto',
        bottomCenter:
          'sm:bottom-0 sm:left-[50%] sm:translate-x-[-50%] sm:top-auto',
      },
      size: {
        default: 'md:max-w-[400px]',
        small: 'md:max-w-[350px]',
        large: 'md:max-w-[420px]',
      },
    },
    defaultVariants: {
      position: 'bottomRight',
      size: 'default',
    },
  },
)

export type ToastViewportPosition = VariantProps<
  typeof toastViewportVariants
>['position']

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport> &
    VariantProps<typeof toastViewportVariants>
>(({ className, position, size, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(toastViewportVariants({ position, size }), className)}
    {...props}
  />
))
ToastViewport.displayName = ToastPrimitives.Viewport.displayName

const toastVariants = cva(
  `group pointer-events-auto relative flex w-full items-center space-x-4 overflow-hidden rounded-md border shadow-lg
   transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] 
   data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in
   data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80
   [data-state=open]:slide-in-from-top-full`,
  {
    variants: {
      variant: {
        default: 'border bg-background text-foreground',
        success:
          'success group border-success bg-success text-success-foreground',
        warning:
          'warning group border-warning bg-warning text-warning-foreground',
        danger: 'danger group border-danger bg-danger text-danger-foreground',
      },
      size: {
        default: 'p-4 pr-6',
        small: 'p-3 pr-4',
        large: 'p-6 pr-8',
      },
      slideIn: {
        topRight:
          'data-[state=open]:sm:slide-in-from-top-full data-[state=closed]:slide-out-to-right-full',
        topLeft:
          'data-[state=open]:sm:slide-in-from-top-full data-[state=closed]:slide-out-to-left-full',
        topCenter:
          'data-[state=open]:sm:slide-in-from-top-full data-[state=closed]:slide-out-to-top-full',
        bottomRight:
          'data-[state=open]:sm:slide-in-from-bottom-full data-[state=closed]:slide-out-to-right-full',
        bottomLeft:
          'data-[state=open]:sm:slide-in-from-bottom-full data-[state=closed]:slide-out-to-left-full',
        bottomCenter:
          'data-[state=open]:sm:slide-in-from-bottom-full data-[state=closed]:slide-out-to-bottom-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      slideIn: 'topRight',
    },
  },
)

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
    VariantProps<typeof toastVariants>
>(({ className, size, variant, slideIn, ...props }, ref) => {
  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(toastVariants({ variant, size, slideIn }), className)}
      {...props}
    />
  )
})
Toast.displayName = ToastPrimitives.Root.displayName

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      `focus:ring-ring group-[.danger]:border-muted/40 inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent
       px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-content4 focus:outline-none focus:ring-2 focus:ring-offset-2 
       disabled:pointer-events-none disabled:opacity-50 group-[.danger]:hover:bg-danger-foreground 
       group-[.danger]:hover:text-danger group-[.danger]:focus:ring-danger`,
      className,
    )}
    {...props}
  />
))
ToastAction.displayName = ToastPrimitives.Action.displayName

const ToastIcon = ({ icon: Icon }: { icon: React.ReactElement }) => Icon
ToastIcon.displayName = 'ToastIcon'

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      `absolute right-2 top-2 rounded-md p-1 text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none 
      focus:ring-2 group-hover:opacity-100 group-[.danger]:text-red-300 group-[.danger]:hover:text-red-50 group-[.danger]:focus:ring-red-400
       group-[.danger]:focus:ring-offset-red-600`,
      className,
    )}
    toast-close=""
    {...props}
  >
    <LuX className="h-4 w-4" />
  </ToastPrimitives.Close>
))
ToastClose.displayName = ToastPrimitives.Close.displayName

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn('text-sm font-semibold', className)}
    {...props}
  />
))
ToastTitle.displayName = ToastPrimitives.Title.displayName

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn('text-sm opacity-90', className)}
    {...props}
  />
))
ToastDescription.displayName = ToastPrimitives.Description.displayName

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>

type ToastActionElement = React.ReactElement<typeof ToastAction>

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
  ToastIcon,
}
